// src/components/EditKasrDaryaftModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import Select from "./ui/Select";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import {
  X,
  Save,
  Upload,
  FileText,
  CalendarDays,
  Hash,
  User,
  UserRound,
  Wallet,
} from "lucide-react";

/* ---------- helpers ---------- */
const cx = (...classes) => classes.filter(Boolean).join(" ");
const firstErr = (v) => (Array.isArray(v) ? v[0] : v);

/* ---------- Select styles (same as form) ---------- */
const useSelectStyles = (Icon) =>
  useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: "44px",
        borderRadius: "0.75rem",
        borderColor: state.isFocused ? "#93C5FD" : "#e2e8f0",
        boxShadow: state.isFocused
          ? "0 0 0 4px rgba(147,197,253,0.35)"
          : "none",
        backgroundColor: "#ffffff",
        paddingRight: Icon ? "34px" : "6px",
        paddingLeft: "6px",
        direction: "rtl",
        textAlign: "right",
        fontFamily: "Vazirmatn, sans-serif",
        ":hover": { borderColor: "#93C5FD" },
      }),
      placeholder: (base) => ({
        ...base,
        color: "#94a3b8",
        textAlign: "right",
      }),
      singleValue: (base) => ({
        ...base,
        color: "#0f172a",
        textAlign: "right",
      }),
      input: (base) => ({ ...base, textAlign: "right", color: "#0f172a" }),
      menu: (base) => ({
        ...base,
        direction: "rtl",
        borderRadius: "0.75rem",
        overflow: "hidden",
        zIndex: 9999,
      }),
      option: (base, state) => ({
        ...base,
        textAlign: "right",
        backgroundColor: state.isSelected
          ? "#2563EB"
          : state.isFocused
          ? "#EFF6FF"
          : "#fff",
        color: state.isSelected ? "#fff" : "#0f172a",
      }),
      indicatorSeparator: (base) => ({ ...base, display: "none" }),
      dropdownIndicator: (base) => ({ ...base, paddingLeft: "10px" }),
      clearIndicator: (base) => ({ ...base, paddingLeft: "10px" }),
    }),
    [Icon]
  );

const SelectField = ({
  id,
  label,
  required,
  icon: Icon,
  isLoading,
  options,
  value,
  onChange,
  placeholder,
  error,
  inputValue,
  onInputChange,
  noOptionsMessage,
}) => {
  const styles = useSelectStyles(Icon);

  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-slate-700 text-right">
        {label}
        {required ? <span className="text-rose-500"> *</span> : null}
      </label>

      <div className="relative">
        {Icon ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 z-10 pointer-events-none">
            <Icon size={18} />
          </span>
        ) : null}

        <Select
          inputId={id}
          name={id}
          value={value}
          onChange={onChange}
          options={options}
          isLoading={!!isLoading}
          isDisabled={!!isLoading}
          isClearable
          placeholder={placeholder}
          noOptionsMessage={noOptionsMessage}
          styles={styles}
          className="text-right"
          inputValue={inputValue}
          onInputChange={onInputChange}
        />
      </div>

      {error ? (
        <div className="text-rose-700 text-sm mt-1 text-right">{error}</div>
      ) : null}
    </div>
  );
};

export default function EditKasrDaryaftModal({
  open,
  loading,
  item,
  form,
  setForm,
  serverErrors,
  customersLoading,
  customersError,
  customerOptions,
  onClose,
  onPlainChange,
  onSave,
}) {
  // IMPORTANT: hooks must run unconditionally (no early return before hooks)

  // same cottage fetching logic as the form
  const [cottages, setCottages] = useState([]);
  const [loadingCottages, setLoadingCottages] = useState(false);
  const [cottageSearch, setCottageSearch] = useState("");
  const reqSeq = useRef(0);

  const hashSelectStyles = useSelectStyles(Hash);

  const fieldError = (key) =>
    serverErrors?.[key] ? firstErr(serverErrors[key]) : "";

  const toSingleCustomer = (id) =>
    customerOptions.find((o) => o.value === String(id)) || null;

  const fetchCottages = async (searchTerm = "") => {
    setLoadingCottages(true);
    const mySeq = ++reqSeq.current;

    try {
      const params = new URLSearchParams();
      if (searchTerm?.trim()) params.set("search", searchTerm.trim());

      const url = `/cottages/${
        params.toString() ? `?${params.toString()}` : ""
      }`;
      const { data } = await axiosInstance.get(url);

      if (mySeq !== reqSeq.current) return;
      setCottages(data?.results || data || []);
    } catch (err) {
      if (mySeq !== reqSeq.current) return;
      setCottages([]);
    } finally {
      if (mySeq === reqSeq.current) setLoadingCottages(false);
    }
  };

  // when opened: fetch initial cottages + reset search
  useEffect(() => {
    if (!open) return;
    fetchCottages("");
    setCottageSearch("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // debounce cottage search
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchCottages(cottageSearch), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cottageSearch, open]);

  // esc + ctrl/cmd+s
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        onSave();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, onSave]);

  const cottageOptions = useMemo(
    () =>
      (cottages || [])
        .map((c) => {
          const num = c.cottage_number ?? c.cottageNumber ?? c.number ?? "";
          return { value: String(num), label: String(num || c.id) };
        })
        .filter((o) => o.value && o.value !== "undefined"),
    [cottages]
  );

  const selectedCottageOption = useMemo(
    () => cottageOptions.find((o) => o.value === form.cottage) || null,
    [cottageOptions, form.cottage]
  );

  const handleSelectInputChange = (inputValue, actionMeta) => {
    if (actionMeta.action === "input-change") setCottageSearch(inputValue);
    return inputValue;
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[999]">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
      />

      <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-6">
        <div className="w-full max-w-4xl rounded-2xl border border-slate-200 bg-white shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div className="text-right">
              <h3 className="text-base font-semibold text-slate-900">
                ویرایش کسر دریافت
              </h3>
              <p className="mt-0.5 text-xs text-slate-500">
                شناسه:{" "}
                <span className="font-medium text-slate-700">{item?.id}</span>
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-lg p-2 text-slate-600 hover:bg-slate-100"
              aria-label="بستن"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="px-4 py-4" dir="rtl">
            {customersError ? (
              <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-right text-rose-700 text-sm">
                خطا در دریافت لیست مشتریان
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <SelectField
                id="declared_to"
                label="اظهار شده به"
                required
                icon={UserRound}
                isLoading={customersLoading}
                options={customerOptions}
                value={toSingleCustomer(form.declared_to)}
                onChange={(opt) =>
                  setForm((p) => ({ ...p, declared_to: opt ? opt.value : "" }))
                }
                placeholder="انتخاب مشتری/شرکت مقصد"
                error={fieldError("declared_to")}
              />

              <SelectField
                id="customer"
                label="مشتری"
                required
                icon={User}
                isLoading={customersLoading}
                options={customerOptions}
                value={toSingleCustomer(form.customer)}
                onChange={(opt) =>
                  setForm((p) => ({ ...p, customer: opt ? opt.value : "" }))
                }
                placeholder="انتخاب مشتری"
                error={fieldError("customer")}
              />

              {/* Cottage (same as form) */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700 text-right">
                  کوتاژ <span className="text-rose-500">*</span>
                </label>

                <div className="relative">
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 z-10 pointer-events-none">
                    <Hash size={18} />
                  </span>

                  <Select
                    name="cottage"
                    value={selectedCottageOption}
                    onChange={(selected) =>
                      setForm((p) => ({
                        ...p,
                        cottage: selected ? selected.value : "",
                      }))
                    }
                    onInputChange={handleSelectInputChange}
                    inputValue={cottageSearch}
                    options={cottageOptions}
                    isLoading={loadingCottages}
                    isClearable
                    placeholder={
                      loadingCottages
                        ? "در حال بارگذاری..."
                        : "جستجو و انتخاب کوتاژ"
                    }
                    noOptionsMessage={() =>
                      loadingCottages ? "در حال بارگذاری..." : "کوتاژ یافت نشد"
                    }
                    styles={hashSelectStyles}
                    className="text-right"
                  />
                </div>

                {fieldError("cottage") ? (
                  <div className="text-rose-700 text-sm mt-1 text-right">
                    {fieldError("cottage")}
                  </div>
                ) : null}
              </div>

              {/* total_value */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700 text-right">
                  مبلغ کل <span className="text-rose-500">*</span>
                </label>

                <div className="relative">
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    <Wallet size={18} />
                  </span>
                  <input
                    name="total_value"
                    type="number"
                    value={form.total_value || ""}
                    onChange={onPlainChange}
                    min={0}
                    step={1}
                    className={cx(
                      "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-right text-slate-900",
                      "placeholder:text-slate-400 shadow-sm",
                      "focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary",
                      "pr-10"
                    )}
                    placeholder="مثلاً 25000000"
                  />
                </div>

                {fieldError("total_value") ? (
                  <div className="text-rose-700 text-sm mt-1 text-right">
                    {fieldError("total_value")}
                  </div>
                ) : null}
              </div>

              {/* date */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700 text-right">
                  تاریخ <span className="text-rose-500">*</span>
                </label>

                <div className="relative">
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                    <CalendarDays size={18} />
                  </span>

                  <DatePicker
                    calendar={persian}
                    locale={persian_fa}
                    value={form.date}
                    onChange={(val) =>
                      setForm((p) => ({
                        ...p,
                        date: val ? val.format("YYYY-MM-DD") : "",
                      }))
                    }
                    format="YYYY-MM-DD"
                    inputClass={cx(
                      "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-right text-slate-900",
                      "placeholder:text-slate-400 shadow-sm",
                      "focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
                    )}
                    containerClassName="w-full"
                    calendarPosition="bottom-right"
                  />
                </div>

                {fieldError("date") ? (
                  <div className="text-rose-700 text-sm mt-1 text-right">
                    {fieldError("date")}
                  </div>
                ) : null}
              </div>

              {/* appeal_deadline */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700 text-right">
                  مهلت اعتراض <span className="text-rose-500">*</span>
                </label>

                <div className="relative">
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                    <CalendarDays size={18} />
                  </span>

                  <DatePicker
                    calendar={persian}
                    locale={persian_fa}
                    value={form.appeal_deadline}
                    onChange={(val) =>
                      setForm((p) => ({
                        ...p,
                        appeal_deadline: val ? val.format("YYYY-MM-DD") : "",
                      }))
                    }
                    format="YYYY-MM-DD"
                    inputClass={cx(
                      "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-right text-slate-900",
                      "placeholder:text-slate-400 shadow-sm",
                      "focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
                    )}
                    containerClassName="w-full"
                    calendarPosition="bottom-right"
                  />
                </div>

                {fieldError("appeal_deadline") ? (
                  <div className="text-rose-700 text-sm mt-1 text-right">
                    {fieldError("appeal_deadline")}
                  </div>
                ) : null}
              </div>

              {/* toggles */}
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-right">
                      <div className="text-sm font-semibold text-slate-800">
                        وضعیت اعتراض
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        اگر اعتراض ثبت شده است، فعال کنید.
                      </div>
                    </div>

                    <label className="relative inline-flex items-center cursor-pointer select-none">
                      <input
                        type="checkbox"
                        name="is_appealed"
                        checked={!!form.is_appealed}
                        onChange={onPlainChange}
                        className="sr-only peer"
                      />
                      <span
                        className="
                          relative w-11 h-6 rounded-full bg-slate-200 transition-colors
                          peer-checked:bg-primary
                          after:content-[''] after:absolute after:top-0.5 after:right-0.5 after:h-5 after:w-5
                          after:rounded-full after:bg-white after:shadow after:transition-transform
                          peer-checked:after:-translate-x-5
                        "
                      />
                      <span className="mr-3 text-sm font-medium text-slate-700">
                        اعتراض شده
                      </span>
                    </label>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-right">
                      <div className="text-sm font-semibold text-slate-800">
                        وضعیت پرداخت
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        در صورت پرداخت/تسویه، فعال کنید.
                      </div>
                    </div>

                    <label className="relative inline-flex items-center cursor-pointer select-none">
                      <input
                        type="checkbox"
                        name="is_paid"
                        checked={!!form.is_paid}
                        onChange={onPlainChange}
                        className="sr-only peer"
                      />
                      <span
                        className="
                          relative w-11 h-6 rounded-full bg-slate-200 transition-colors
                          peer-checked:bg-primary
                          after:content-[''] after:absolute after:top-0.5 after:right-0.5 after:h-5 after:w-5
                          after:rounded-full after:bg-white after:shadow after:transition-transform
                          peer-checked:after:-translate-x-5
                        "
                      />
                      <span className="mr-3 text-sm font-medium text-slate-700">
                        پرداخت شده
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* document */}
              <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-800">
                      فایل پیوست
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      تصویر یا PDF (اختیاری)
                    </div>
                  </div>
                  <div className="text-slate-400 mt-0.5">
                    <Upload size={18} />
                  </div>
                </div>

                <div className="mt-3">
                  <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-dashed border-slate-300 bg-white px-3 py-3 hover:bg-slate-50">
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <Upload size={18} />
                      {form.document instanceof File
                        ? form.document.name
                        : "انتخاب فایل جدید"}
                    </div>
                    <input
                      type="file"
                      name="document"
                      onChange={onPlainChange}
                      className="hidden"
                    />
                  </label>
                </div>

                {fieldError("document") ? (
                  <div className="text-rose-700 text-sm mt-1 text-right">
                    {fieldError("document")}
                  </div>
                ) : null}

                {item?.document ? (
                  <div className="mt-3">
                    <a
                      href={item.document}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      <FileText size={16} />
                      مشاهده فایل فعلی
                    </a>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-4 py-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              <X size={16} />
              انصراف
            </button>

            <button
              type="button"
              onClick={onSave}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-4 py-2 text-sm text-white hover:bg-sky-700 disabled:opacity-60"
            >
              <Save size={16} />
              {loading ? "در حال ذخیره..." : "ذخیره تغییرات"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
